import { useState } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { ChevronRight, FileText, LayoutDashboard, Menu, Plus, Sparkles } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer"
import { useSidebar } from "@/components/ui/sidebar"
import { quickActions } from "@/config/navigation"
import { useRouteMatches } from "@/components/layout/route-handle"
import { useReviewCount } from "@/hooks/use-ai-generations"
import { cn } from "@/lib/utils"

interface TabItem {
  title: string
  href: string
  icon: LucideIcon
  /** Extra path prefixes that count as this tab being active. */
  match: string[]
  end?: boolean
}

const TABS: [TabItem, TabItem, TabItem] = [
  { title: "Home", href: "/dashboard", icon: LayoutDashboard, match: [], end: true },
  { title: "Posts", href: "/dashboard/posts", icon: FileText, match: ["/dashboard/posts"] },
  { title: "AI", href: "/dashboard/ai-generations", icon: Sparkles, match: ["/dashboard/ai-generations", "/dashboard/trends", "/dashboard/calendar"] },
]

function isTabActive(tab: TabItem, pathname: string) {
  if (tab.end) return pathname === tab.href
  return tab.match.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

function TabLink({ tab, badge }: { tab: TabItem; badge?: number }) {
  const { pathname } = useLocation()
  const active = isTabActive(tab, pathname)
  return (
    <NavLink
      to={tab.href}
      end={tab.end}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors",
        active ? "text-foreground" : "text-muted-foreground active:text-foreground",
      )}
    >
      <span className={cn("flex h-7 w-12 items-center justify-center rounded-full border border-transparent transition-colors", active && "border-border bg-secondary")}>
        <tab.icon className="size-5" />
      </span>
      {tab.title}
      {badge ? (
        <span className="absolute top-1 left-1/2 ml-2 min-w-4 rounded-full bg-foreground px-1 text-center text-[10px] leading-4 font-semibold text-background tabular-nums">
          {badge > 9 ? "9+" : badge}
        </span>
      ) : null}
    </NavLink>
  )
}

/**
 * Phone-only bottom tab bar: the three most-used destinations, a create
 * sheet, and "Menu" for everything else (opens the sidebar drawer).
 */
export function MobileNav() {
  const navigate = useNavigate()
  const matches = useRouteMatches()
  const { pathname } = useLocation()
  const { openMobile, setOpenMobile } = useSidebar()
  const { count: reviewCount } = useReviewCount()
  const [createOpen, setCreateOpen] = useState(false)

  if (matches.some((match) => match.handle?.hideMobileNav)) return null

  const menuActive = openMobile || !TABS.some((tab) => isTabActive(tab, pathname))

  return (
    <>
      <nav
        aria-label="Primary"
        className="shrink-0 border-t bg-background/95 pb-safe backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden"
      >
        <div className="grid h-16 grid-cols-5">
          <TabLink tab={TABS[0]} />
          <TabLink tab={TABS[1]} />
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              aria-label="Create"
              aria-haspopup="dialog"
              className="flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-[transform,background-color] hover:bg-primary-hover active:scale-95 active:bg-primary-pressed"
            >
              <Plus className="size-6" />
            </button>
          </div>
          <TabLink tab={TABS[2]} badge={reviewCount} />
          <button
            type="button"
            onClick={() => setOpenMobile(true)}
            aria-label="Open menu"
            className={cn(
              "flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors",
              menuActive ? "text-foreground" : "text-muted-foreground active:text-foreground",
            )}
          >
            <span className={cn("flex h-7 w-12 items-center justify-center rounded-full border border-transparent", menuActive && "border-border bg-secondary")}>
              <Menu className="size-5" />
            </span>
            Menu
          </button>
        </div>
      </nav>

      <Drawer open={createOpen} onOpenChange={setCreateOpen}>
        <DrawerContent className="pb-safe">
          <DrawerHeader className="text-left">
            <DrawerTitle>Create</DrawerTitle>
            <DrawerDescription>Start something new</DrawerDescription>
          </DrawerHeader>
          <ul className="space-y-1 px-2 pb-4">
            {quickActions.map((action) => (
              <li key={action.href}>
                <button
                  type="button"
                  onClick={() => {
                    setCreateOpen(false)
                    navigate(action.href)
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors active:bg-muted"
                >
                  <span className="flex size-10 items-center justify-center rounded-lg border bg-secondary text-ink-2">
                    <action.icon className="size-5" />
                  </span>
                  {action.title}
                  <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        </DrawerContent>
      </Drawer>
    </>
  )
}
